import React from "react";
import ArticleTemplate from "../../../components/ArticleTemplate";

const contentHtml = `
<h1>The $3 Billion Loss Year: End-of-Year Security Report</h1>
<p>2025 made it unmistakably clear: in Web3, security is no longer a background concern or merely a checkbox; it is the defining factor separating the projects that survived from the ones that collapsed. With an estimation of more than <strong>$3 billion</strong> in losses across hacks, scams, protocol failures, and key management breaches this year, the ecosystem was forced into a harsh confrontation with its own maturity. While innovation accelerated, attacker sophistication and manoeuvring attacks outpaced both, revealing fundamental weaknesses that are no longer optional to address.
p=
In Extropy, as a security auditing firm working across L1s, L2s, DeFi, gaming, and ZK applications, we reviewed real codebases and attack surfaces that could have resulted in catastrophic losses. We offer a picture of where Web3  security stands today and what development teams must prioritise going into 2026.</p>
<hr>
<h2>Some of the issues our audits uncovered this year</h2>
<p>Our audits throughout the year exposed a pattern: even well-funded teams with experienced developers repeatedly fell into the same high-impact traps.</p>
<h3>1. Incorrect Reward Accounting in Move Modules</h3>
<p>One of the most consequential findings occurred within a Move-based staking system due to a subtle accounting error in the reward distribution logic.</p>
<ul>
<li><strong>The Issue:</strong> The function incorrectly subtracted the <code>collectable_reward</code> from the total removed value without isolating the principal from the reward flow.</li>
<li><strong>Code Snippet:</strong></li>
</ul>
<pre><code class="language-move">// native_pool.move (excerpt)
let collectable_reward = reward_amounts[i];
total_removed_value = total_removed_value - collectable_reward; // Incorrect deduction

</code></pre>
<ul>
<li><strong>Impact:</strong> This failure to treat principal and rewards as distinct streams leads to corrupted validator balances, systemic reward imbalances (insolvency), and arithmetic cascades during subsequent cycles.</li>
</ul>
<h3>2. total_supply Underflow Causing Global DoS (Move)</h3>
<p>We identified a high-severity vulnerability in a ticket-based staking protocol where critical global state variables were manipulated before validation.</p>
<ul>
<li><strong>The Issue:</strong> The <code>total_supply</code> was modified in the local execution context before verifying the user's available balance.</li>
<li><strong>Impact:</strong> If the <code>ticket_amount</code> exceeds the balance, the transaction correctly reverts, but only after the <code>total_supply</code> has been modified. In specific runtime environments, this can lead to an underflow that freezes the contract's core functionality.</li>
</ul>
<h3>3. Settlement-Phase Financial Manipulation (Solidity)</h3>
<p>During a Solidity audit, we uncovered a dangerous settlement design where external interactions and internal state updates were intermingled.</p>
<ul>
<li><strong>The Issue:</strong> Relying solely on the <code>onlyAdmin</code> modifier created a "security-by-access-control" fallacy that ignored the risks of compromised or malicious admin keys.</li>
<li><strong>Code Snippet:</strong></li>
</ul>
<pre><code class="language-solidity">function settle() external onlyAdmin {
    uint256 payout = calculatePayout(user);
    userBalances[user] -= payout; // Updated before final validation
    (bool ok,) = user.call{value: payout}(""); // Mixed with external call
}

</code></pre>
<ul>
<li><strong>Impact:</strong> This allowed for state inconsistency and atomic misordering, demonstrating that security must be enforced at the state level.</li>
</ul>
<h3>4. Resolver / State Desynchronisation in ZK Games</h3>
<p>In ZK-powered gaming systems (Mina/o1js), we identified a recurring structural weakness involving desynchronisation between off-chain resolvers and on-chain verification.</p>
<ul>
<li><strong>The Issue:</strong> The backend infrastructure updated game states independently of the zkApp's on-chain validation.</li>
<li><strong>Impact:</strong> Without a cryptographic "handshake," the system was vulnerable to forged state transitions and replay inconsistencies.</li>
</ul>
<h3>5. Lack of Replay Protection &#x26; Weak Message Identity</h3>
<p>A pervasive issue across multi-chain and intent-based systems was the absence of robust message-identity controls.</p>
<ul>
<li><strong>The Issue:</strong> Hashing functions lacked domain separators or nonces, making signatures vulnerable to collision or forgery.</li>
<li><strong>Impact:</strong> Signatures authorized for an action on one chain could be replayed to drain funds on another, a primary driver of cross-chain liquidity loss in 2025.</li>
</ul>
<hr>
<h2>Analysis: The State of Web3 Security</h2>
<p>These findings reveal a fundamental shift in the threat landscape where attackers have pivoted toward architectural exploitation.</p>
<ul>
<li><strong>Failure of "Safe-by-Default":</strong> Over-reliance on the safety of Move and Rust led to "psychological safety," where developers assumed compilation precluded the need for invariant enforcement.</li>
<li><strong>Verification Gap:</strong> In ZK systems, the weakest link is often the off-chain resolver—the bridge that must maintain consistency between the proof, the contract, and the server.</li>
<li><strong>Operational &#x26; Admin Risk:</strong> Privileged admin pathways were responsible for approximately <strong>$1.6 billion (70%)</strong> of stolen funds in the first half of 2025.</li>
<li><strong>Human-Centric Vectors:</strong> Roughly <strong>40% of losses</strong> occurred via phishing, unauthorised permit signatures, and front-running, indicating that operational workflows remain highly vulnerable.</li>
</ul>
<hr>
<h2>Recommendations for 2026</h2>
<ol>
<li><strong>Defensive Architecture:</strong> Implement automated on-chain invariant checks and algorithmic circuit breakers to degrade gracefully during attacks.</li>
<li><strong>Precision Access Control:</strong> Move toward granular, time-locked capabilities and enforce "Least Privilege" for dev-ops pipelines.</li>
<li><strong>Identity Integrity:</strong> Mandate EIP-712 domain separation and ensure off-chain resolvers are cryptographically bound to the on-chain source of truth.</li>
<li><strong>User Protection:</strong> Integrate transaction simulation into UIs to prevent "blind-signing" and conduct adversarial UX testing against phishing.</li>
</ol>
<hr>
<h3>Future Outlook</h3>
<p>In 2026, we expect security to serve as a core business differentiator. As highlighted at Abu Dhabi Fintech Week 2025, institutional adoption hinges on provable resilience. The threat landscape will continue to professionalise with AI-driven social engineering and autonomous predator swarms, reinforcing that Web3 security is now full-stack system security.</p>
<h2>Final Words</h2>
<p>The future of Web3 security is not about the total elimination of risk, but about the rigorous containment and engineering of that risk.
The tools exist; the remaining question is whether development teams will treat security as an indispensable foundation or an afterthought.
Over the next few weeks will be supplying security guidelines and checklists for Web3 developers to ensure that your start to 2026 is a secure one.</p>

`;

const eoyReport2025 = () => {
  return (
    <ArticleTemplate
      title="The $3 Billion Loss Year: End-of-Year Security Report"
      subtitle=""
      author="Extropy Security Team"
      date="Dec 31st, 2025"
      readingTime="12 min read"
      tags={["Article","Audit","Security"]}
      canonicalUrl="https://security.extropy.io/articles/eoy-security-report-2025"
      contentHtml={contentHtml}
    />
  );
};

export default eoyReport2025;
